/** Telegraphs (fill up over their delay) and zones (persistent pools) as translucent ground shapes. */
import * as THREE from 'three';
import type { AreaShape } from '@keystone/sim';
import type { RenderArea } from '../types';
import { toThree } from './coords';

/** Geometry in local XY: origin at (0,0), facing +x. */
function shapeGeometry(s: AreaShape): THREE.BufferGeometry {
  switch (s.type) {
    case 'circle':
      return new THREE.CircleGeometry(s.radius, 48);
    case 'ring':
      return new THREE.RingGeometry(s.inner, s.outer, 48);
    case 'cone':
      return new THREE.CircleGeometry(s.range, 32, -s.angle / 2, s.angle);
    case 'line':
      return new THREE.PlaneGeometry(s.length, s.width).translate(s.length / 2, 0, 0);
    case 'rect':
      return new THREE.PlaneGeometry(s.h, s.w);
  }
}

let layerSeq = 0;

const HOSTILE_TELEGRAPH = 0xff5a2a;
const FRIENDLY_TELEGRAPH = 0x5ab0ff;
const HOSTILE_ZONE = 0x9c3cff;
const FRIENDLY_ZONE = 0xf1c84a;

export class AreaView {
  readonly group = new THREE.Group();
  private readonly base: THREE.Mesh;
  private readonly fill: THREE.Mesh;
  private readonly edge: THREE.LineSegments;
  private readonly shape: AreaShape;

  constructor(a: RenderArea) {
    this.shape = a.area.shape;
    const geo = shapeGeometry(this.shape);
    const tint = a.kind === 'telegraph' ? (a.hostile ? HOSTILE_TELEGRAPH : FRIENDLY_TELEGRAPH) : a.hostile ? HOSTILE_ZONE : FRIENDLY_ZONE;
    const mat = (opacity: number): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    this.base = new THREE.Mesh(geo, mat(a.kind === 'telegraph' ? 0.16 : 0.22));
    this.fill = new THREE.Mesh(geo, mat(a.kind === 'telegraph' ? 0.38 : 0));
    this.fill.visible = a.kind === 'telegraph';
    this.edge = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: tint, transparent: true, opacity: 0.85 }));
    // Inner holder lays the XY shape flat; the outer group carries position + facing.
    const flat = new THREE.Group();
    flat.rotation.x = -Math.PI / 2;
    flat.add(this.base, this.fill, this.edge);
    this.group.add(flat);
    // Stagger heights slightly to avoid z-fighting between overlapping areas.
    this.group.position.y = 0.05 + (layerSeq++ % 16) * 0.002;
  }

  update(a: RenderArea, timeMs: number): void {
    const y = this.group.position.y;
    toThree(a.area.origin, y, this.group.position);
    this.group.rotation.y = a.area.facing;
    if (a.kind === 'telegraph') {
      const p = Math.max(0.001, a.progress);
      const s = this.shape.type;
      if (s === 'circle' || s === 'cone') this.fill.scale.set(p, p, 1);
      else if (s === 'line') this.fill.scale.set(p, 1, 1);
      else if (s === 'rect') this.fill.scale.set(p, 1, 1);
      else (this.fill.material as THREE.MeshBasicMaterial).opacity = 0.1 + 0.4 * p;
    } else {
      const pulse = 0.18 + 0.06 * Math.sin(timeMs / 220);
      (this.base.material as THREE.MeshBasicMaterial).opacity = pulse * Math.min(1, 0.4 + a.progress);
    }
  }

  dispose(): void {
    this.base.geometry.dispose();
    (this.base.material as THREE.Material).dispose();
    (this.fill.material as THREE.Material).dispose();
    this.edge.geometry.dispose();
    (this.edge.material as THREE.Material).dispose();
  }
}
